import { createSelector } from 'reselect';
import {
  selectEdgeFilters,
  selectEdgeFilterType,
  selectHiddenNodes,
  selectNodeFilterOut,
  selectNodeFilters,
  selectNodeFiltersOptions,
  selectNodeFilterType,
  selectRegionFilter,
  selectShortestPathNodeId,
  selectTimeFilters,
  selectAllContextFilters,
  selectAffiliationClusterMode,
} from 'store/filters/selectors';
import { IsObjectEmpty } from 'utils/graph-helper';
import { applySelectionStylesToItems, selectItemsHelper } from './graph-items-selector-helper';

const graph = (state) => state.graph;

export const selectGraph = createSelector([graph], (state) => state);

export const selectNodes = createSelector([graph], (state) => state.nodes);

export const selectNodeProperties = (nodeId) =>
  createSelector([graph], (state) => {
    return state.nodes[nodeId]?.node?.data;
  });

export const selectLinks = createSelector([graph], (state) => state.links);

export const selectContextIds = createSelector(
  [graph],
  (state) => state.contextIds,
);

export const selectMoreThanOneContext = createSelector(
  [graph],
  (state) => state.contextIds.length > 1,
);

export const selectIsContextIdsEmpty = createSelector([graph], (state) =>
  IsObjectEmpty(state.contextIds),
);
export const selectTopConnectionsLoading = createSelector(
  [graph],
  (state) => state.topConnectionsLoading,
);

export const selectTopConnections = createSelector([graph], (state) => {
  return state.topConnections[state.selectedVertex];
});

export const selectMostConnectedLoading = createSelector(
  [graph],
  (state) => state.mostConnectedLoading,
);

export const selectMostConnected = createSelector([graph], (state) => {
  return state.mostConnected[state.selectedVertex];
});

export const selectAiContext = createSelector([graph], (state) => {
  return state.aiContext;
});

export const selectContextsObject = createSelector([graph], (state) => {
  return state.contexts;
});

export const selectContexts = createSelector([graph], (state) => {
  const { contexts } = state;
  return Object.values(contexts).map((context) => context.value);
});

export const selectAiContexts = (contextId) =>
  createSelector([graph], (state) => {
    const { aiContext } = state;
    return aiContext.find((context) => context.id === contextId);
  });

export const selectHasAiContext = createSelector(
  [selectContexts],
  (contexts) => {
    return contexts.some((context) => context.ai_search);
  },
);

export const selectContextsByContextId = (nodeId) =>
  createSelector([graph], (state) => {
    const { contexts } = state;
    const context = contexts[nodeId];
    if (context) {
      return context.value;
    }
    return undefined;
  });

export const selectContextForIndividualFilters = (id) =>
  createSelector([graph], (state) => {
    const { contexts, nodes } = state;
    return { context: contexts[id]?.value, node: nodes[id]?.node };
  });

export const IsGraphEmpty = createSelector([graph], (state) =>
  IsObjectEmpty(state.contexts),
);

export const selectContextsForItems = createSelector([graph], (state) => {
  const { contexts } = state;
  return contexts;
});

export const selectGraphData = createSelector(
  [
    selectNodes,
    selectLinks,
    selectContextIds,
    selectContextsForItems,
    selectHiddenNodes,
  ],
  (nodes, links, contextIds, contexts, hiddenNodes) => ({
    nodes,
    links,
    contextIds,
    contexts,
    hiddenNodes,
  }),
);

export const selectItems = createSelector(
  [
    selectGraphData,
    selectEdgeFilters,
    selectEdgeFilterType,
    selectNodeFiltersOptions,
    selectNodeFilterType,
    selectRegionFilter,
    selectNodeFilters,
    selectNodeFilterOut,
    selectShortestPathNodeId,
    selectTimeFilters,
    selectAllContextFilters,
    selectAffiliationClusterMode,
  ],
  (
    graphData,
    filtersForEdges,
    edgeFilterType,
    nodeOptions,
    nodeFilterType,
    regions,
    nodeFilters,
    nodeTypeFilterOut,
    shortestPathNodeId,
    timeFilters,
    contextFilters,
    affiliationClusterMode,
  ) => {
    const {
      nodes,
      links,
      contextIds,
      contexts,
      hiddenNodes,
    } = graphData;
    return selectItemsHelper(
      nodes,
      links,
      contextIds,
      filtersForEdges,
      edgeFilterType,
      nodeOptions,
      nodeFilterType,
      regions,
      nodeFilters,
      nodeTypeFilterOut,
      shortestPathNodeId,
      timeFilters,
      contexts,
      hiddenNodes,
      contextFilters,
      affiliationClusterMode,
    );
  },
);

export const selectStyledItems = createSelector(
  [
    selectItems,
    selectNodes,
    selectNodeFiltersOptions,
    selectNodeFilterType,
    selectNodeFilterOut,
    selectShortestPathNodeId,
  ],
  (
    items,
    nodes,
    nodeOptions,
    nodeFilterType,
    nodeTypeFilterOut,
    shortestPathNodeId,
  ) =>
    applySelectionStylesToItems(
      items,
      nodes,
      nodeOptions,
      nodeFilterType,
      nodeTypeFilterOut,
      shortestPathNodeId,
    ),
);

export const selectSelectedVertex = createSelector([graph], (state) => {
  const { nodes, suggestedNodes, selectedVertex } = state;
  const nodeData = nodes[selectedVertex]?.node?.data;
  if (nodeData) {
    return nodeData;
  }
  return suggestedNodes[selectedVertex]?.data;
});

export const selectSelectedVertexId = createSelector(
  [graph],
  (state) => state.selectedVertex,
);

export const selectSearchContext = createSelector(
  [graph],
  (state) => state.searchContext,
);

export const selectSelectedEdgeId = createSelector(
  [graph],
  (state) => state.selectedEdgeId,
);
// top node is always outNode, bottom node is inNode
// id1 is always inNode
export const selectSelectedEdge = createSelector(
  [selectLinks, selectNodes, selectSelectedEdgeId],
  (links, nodes, selectedEdgeId) => {
    const edge = links[selectedEdgeId]?.link;
    if (edge) {
      const { data, id1, id2 } = edge;
      const inNode = nodes[id1]?.node.data;
      const outNode = nodes[id2]?.node.data;
      const {
        relationship,
        connectionType,
        topNodeRelationshipType,
        bottomNodeRelationshipType,
      } = data;

      return {
        data: { ...data, connectionType, relationship },
        topNode: {
          ...outNode,
          nodeRelationshipType: topNodeRelationshipType,
        },
        bottomNode: {
          ...inNode,
          nodeRelationshipType: bottomNodeRelationshipType,
        },
      };
    }
    return undefined;
  },
);

export const selectIsLoading = createSelector(
  [graph],
  (state) => state.isLoading,
);

export const selectFocusedNode = createSelector(
  [graph],
  (state) => state.focusedNode,
);

export const selectFocusPingTick = createSelector(
  [graph],
  (state) => state.focusPingTick,
);

export const selectSuggestedNodes = createSelector(
  [graph],
  (state) => state.suggestedNodes,
);

export const selectSuggestedEdges = createSelector(
  [graph],
  (state) => state.suggestedEdges,
);

export const selectSuggestionsLoading = createSelector(
  [graph],
  (state) => state.suggestionsLoading,
);

export const selectSuggestionsError = createSelector(
  [graph],
  (state) => state.suggestionsError,
);

export const selectSelectedSuggestedEdge = createSelector(
  [graph],
  (state) => state.selectedSuggestedEdge,
);

export const selectSelectionState = createSelector(
  [selectSelectedVertexId, selectSelectedEdgeId, selectSelectedSuggestedEdge],
  (selectedVertex, selectedEdgeId, selectedSuggestedEdge) => ({
    selectedVertex,
    selectedEdgeId,
    selectedSuggestedEdge,
  }),
);

export const selectSuggestedEdgeByKey = (key) => createSelector(
  [selectSuggestedEdges],
  (edges) => edges[key],
);

export const selectConfirmationResult = createSelector(
  [graph],
  (state) => state.confirmationResult,
);

export const selectNodeById = (nodeId) => createSelector(
  [selectNodes, selectSuggestedNodes],
  (nodes, suggestedNodes) => nodes[nodeId]?.node || suggestedNodes[nodeId],
);
